import React, { useCallback, useEffect, useImperativeHandle, useRef, forwardRef } from 'react';

/**
 * Cloudflare Turnstile widget.
 *
 * Dependency-free wrapper around the Turnstile script, using explicit rendering
 * so the widget lifecycle is tied to this component rather than to a global
 * auto-scan of the DOM (which misbehaves when the form mounts/unmounts).
 *
 * The token this produces is verified server-side in
 * backend/utils/spam_guard.py:verify_turnstile. A token is single-use and
 * short-lived, so the parent should reset the widget after a failed submit.
 *
 * If REACT_APP_TURNSTILE_SITE_KEY is not set the component renders nothing and
 * stays out of the way. Note that the site key is read at BUILD time by CRA, so
 * the frontend must be rebuilt after setting it.
 */

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Shared across every instance so the script is only ever fetched once.
let scriptPromise = null;

const loadTurnstileScript = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile requires a browser environment'));
  }

  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const script = existing || document.createElement('script');

    const handleLoad = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        reject(new Error('Turnstile script loaded but API is unavailable'));
      }
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', () => {
      // Allow a later retry rather than caching the failure forever.
      scriptPromise = null;
      reject(new Error('Failed to load Turnstile script'));
    });

    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return scriptPromise;
};

export const isTurnstileConfigured = () => Boolean(SITE_KEY);

const Turnstile = forwardRef(({
  onVerify,
  onExpire,
  onError,
  className = '',
  theme = 'light',
  size = 'flexible',
}, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const isMountedRef = useRef(true);

  // Keep callbacks in refs so re-renders of the parent never force the widget to
  // be torn down and re-rendered (which would invalidate a pending token).
  const callbacksRef = useRef({ onVerify, onExpire, onError });
  useEffect(() => {
    callbacksRef.current = { onVerify, onExpire, onError };
  }, [onVerify, onExpire, onError]);

  const reset = useCallback(() => {
    if (window.turnstile && widgetIdRef.current !== null) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (error) {
        console.warn('Turnstile reset failed:', error);
      }
    }
  }, []);

  useImperativeHandle(ref, () => ({ reset }), [reset]);

  useEffect(() => {
    if (!SITE_KEY) {
      return undefined;
    }

    isMountedRef.current = true;

    loadTurnstileScript()
      .then((turnstile) => {
        // Guard against the mount/unmount cycle React 18 StrictMode performs in
        // development, and against a slow script load after navigation away.
        if (!isMountedRef.current || !containerRef.current || widgetIdRef.current !== null) {
          return;
        }

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          theme,
          size,
          callback: (token) => callbacksRef.current.onVerify?.(token),
          'expired-callback': () => {
            callbacksRef.current.onExpire?.();
          },
          'error-callback': () => {
            callbacksRef.current.onError?.();
          },
        });
      })
      .catch((error) => {
        console.error('Turnstile unavailable:', error);
        callbacksRef.current.onError?.(error);
      });

    return () => {
      isMountedRef.current = false;
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (error) {
          // Widget already gone; nothing to clean up.
        }
        widgetIdRef.current = null;
      }
    };
  }, [theme, size]);

  if (!SITE_KEY) {
    return null;
  }

  return <div ref={containerRef} className={className} />;
});

Turnstile.displayName = 'Turnstile';

export default Turnstile;
